/**
 * Dove portare l'utente quando tocca una notifica.
 *
 * ## Il contratto vero, letto dal backend
 *
 * `NotificationService.BuildPushDataDictionary` mette nel messaggio FCM **due sole chiavi**:
 *
 * - `pushType` — il tipo, fra gli otto che il BE emette oggi;
 * - `pushData` — una **stringa JSON**, non un oggetto: dentro ci sono almeno `title` e `body`, più
 *   quello che il chiamante ha messo in `Notification.Data`.
 *
 * Quindi `dati.pushData.id` non esiste: va prima riparsato. È il genere di dettaglio che, dato per
 * scontato, produce una navigazione verso `undefined`.
 *
 * ## Perché una destinazione conservativa
 *
 * Le rotte qui sotto sono solo quelle di cui si è certi guardando il tipo. Per tutto il resto non si
 * naviga: l'app resta dov'è e la campanella mostra la notifica appena arrivata. Mandare qualcuno su
 * una pagina sbagliata è peggio che non spostarlo — perde il posto in cui stava, e la cosa
 * annunciata non la trova comunque.
 */
import { notificationLink } from '@/features/notifications/api';

/** I tipi con un nome esatto; le famiglie composte (`leaveApproved`, …) le copre il prefisso. */
export const PUSH_TYPES = [
  'vac',
  'refreshNotifications',
  'portal_site_report_created',
  'portal_document_published',
  'order_sub_activities_provisioned',
  'leave_document_overdue',
  'helpDeskResp',
  'ATTENDANCE_MISSED_PUNCH',
] as const;

/**
 * La destinazione la decide `notificationLink`, che è **la stessa mappa** usata dalla campanella.
 *
 * Erano due elenchi separati per la stessa domanda — «dove porta questo avviso» — e infatti
 * dicevano cose diverse: qui otto tipi esatti, di là le famiglie composte a runtime. Due mappe
 * della stessa cosa divergono al primo tipo nuovo, e il tocco su una push finiva altrove rispetto
 * al clic sulla stessa notifica nel pannello.
 *
 * Resta fuori solo `refreshNotifications`, che non è un avviso ma un comando di aggiornamento, e
 * `order_sub_activities_provisioned`: riguarda commesse e attività, e senza sapere **quale** id il
 * BE metta in `Data` una rotta di elenco sarebbe una scommessa.
 */

export interface PushDestination {
  /** La rotta, o `null` se non si naviga. */
  route: string | null;
  /** Sempre vero tranne che per i comandi: la campanella va comunque ricaricata. */
  reloadNotifications: boolean;
}

/** Il JSON dentro `pushData`, se c'è e se è leggibile. */
export function readPushData(data: Record<string, unknown>): Record<string, unknown> | null {
  const raw = data.pushData;
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const obj: unknown = JSON.parse(raw);
    return obj && typeof obj === 'object' && !Array.isArray(obj) ? obj as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

/** Dove andare, dato il `data` grezzo della notifica. Non solleva su nulla. */
export function pushDestination(dati: Record<string, unknown> | null | undefined): PushDestination {
  const tipo = typeof dati?.pushType === 'string' ? dati.pushType : '';

  if (tipo === 'refreshNotifications') return { route: null, reloadNotifications: true };

  const payload = typeof dati?.pushData === 'string' ? dati.pushData : undefined;
  return { route: notificationLink({ pushType: tipo, data: payload }) ?? null, reloadNotifications: true };
}
